import { profileUrl, qrMatrix } from './profileQr';
const mm = 72 / 25.4;
// Single A4 image page; physical dimensions are PDF points, not browser CSS pixels.
export function imagePdf(bytes, width, height) {
  const encoder = new TextEncoder();
  const objects = [
    encoder.encode('<< /Type /Catalog /Pages 2 0 R >>'),
    encoder.encode('<< /Type /Pages /Kids [3 0 R] /Count 1 >>'),
    encoder.encode(
      `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${210 * mm} ${297 * mm}] /Resources << /XObject << /Im0 4 0 R >> >> /Contents 5 0 R >>`,
    ),
    new Blob([
      `<< /Type /XObject /Subtype /Image /Width ${width} /Height ${height} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /FlateDecode /Length ${bytes.length} >>\nstream\n`,
      bytes,
      '\nendstream',
    ]),
  ];
  const commands = `q ${210 * mm} 0 0 ${297 * mm} 0 0 cm /Im0 Do Q`;
  objects.push(
    encoder.encode(
      `<< /Length ${encoder.encode(commands).length} >>\nstream\n${commands}\nendstream`,
    ),
  );
  const chunks = [encoder.encode('%PDF-1.4\n%Postpop\n')],
    offsets = [0];
  let offset = chunks[0].length;
  objects.forEach((o, i) => {
    offsets.push(offset);
    const a = encoder.encode(`${i + 1} 0 obj\n`),
      b = encoder.encode('\nendobj\n');
    chunks.push(a, o, b);
    offset += a.length + (o.size ?? o.length) + b.length;
  });
  chunks.push(
    encoder.encode(
      `xref\n0 6\n0000000000 65535 f \n${offsets
        .slice(1)
        .map((n) => String(n).padStart(10, '0') + ' 00000 n \n')
        .join('')}trailer\n<< /Size 6 /Root 1 0 R >>\nstartxref\n${offset}\n%%EOF\n`,
    ),
  );
  return new Blob(chunks, { type: 'application/pdf' });
}
export async function printArtwork(front, { nickname, showName, siteUrl, signal }) {
  if (signal?.aborted) throw signal.reason ?? new Error('Cancelled');
  const url = profileUrl(nickname, showName, siteUrl),
    matrix = url ? qrMatrix(url) : null;
  if (typeof CompressionStream === 'undefined') throw Error('PDF compression unavailable');
  await Promise.all([
    document.fonts.load('400 40px Pretendard'),
    document.fonts.load('600 40px Pretendard'),
  ]);
  if (signal?.aborted) throw signal.reason ?? new Error('Cancelled');
  const image = await createImageBitmap(front);
  try {
    if (image.width !== 1080 || image.height !== 1509) throw Error('Pocket card required');
    const canvas = document.createElement('canvas');
    canvas.width = 2480;
    canvas.height = 3508;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw Error('Canvas unavailable');
    const scale = canvas.width / 210;
    ctx.scale(scale, scale);
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, 210, 297);
    function text(value, x, y, w, h, size, bold = false) {
      let lines = [],
        font = size;
      for (; font >= 6; font -= 0.5) {
        ctx.font = `${bold ? 600 : 400} ${font / mm}px Pretendard`;
        lines = [];
        for (const para of value.split('\n')) {
          let line = '';
          for (const char of Array.from(para)) {
            if (line && ctx.measureText(line + char).width > w) {
              lines.push(line);
              line = '';
            }
            line += char;
          }
          lines.push(line);
        }
        if (((lines.length * font) / mm) * 1.25 <= h) break;
      }
      font = Math.max(6, font);
      ctx.save();
      ctx.beginPath();
      ctx.rect(x, y, w, h);
      ctx.clip();
      ctx.fillStyle = '#000';
      ctx.textBaseline = 'top';
      const step = (font / mm) * 1.25;
      lines.forEach((l, i) => ctx.fillText(l, x, y + i * step));
      ctx.restore();
    }
    text('postpop / Your Own Archive', 31, 25, 155, 14, 20, true);
    text('63 x 88 mm / FRONT + BACK / Print at 100% (Actual Size)', 31, 43, 155, 8, 10);
    // Extend interior edge pixels into 3 mm bleed; never enlarge/crop the front design.
    const sx = [2, 0, image.width - 3],
      sy = [2, 0, image.height - 3],
      sw = [1, image.width, 1],
      sh = [1, image.height, 1],
      dx = [28, 31, 94],
      dy = [62, 65, 153],
      dw = [3, 63, 3],
      dh = [3, 88, 3];
    for (let r = 0; r < 3; r++)
      for (let c = 0; c < 3; c++)
        ctx.drawImage(image, sx[c], sy[r], sw[c], sh[r], dx[c], dy[r], dw[c], dh[r]);
    ctx.fillStyle = '#fff';
    ctx.fillRect(113, 62, 69, 94);
    text('postpop', 122, 73, 51, 10, 22, true);
    text('Your Own Archive', 122, 87, 51, 8, 9);
    text(showName ? Array.from(nickname).slice(0, 60).join('') : '', 122, 98, 51, 13, 12, true);
    if (matrix) {
      const n = matrix.length + 8,
        module = 26 / n,
        x = 147.5 - 13,
        y = 113;
      ctx.fillStyle = '#000';
      ctx.save();
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      for (let row = 0; row < matrix.length; row++)
        for (let col = 0; col < matrix.length; col++)
          if (matrix[row][col]) {
            const l = Math.round((x + (col + 4) * module) * scale),
              t = Math.round((y + (row + 4) * module) * scale),
              right = Math.round((x + (col + 5) * module) * scale),
              bottom = Math.round((y + (row + 5) * module) * scale);
            ctx.fillRect(l, t, right - l, bottom - t);
          }
      ctx.restore();
      text('SCAN MY MUSIC PROFILE', 122, 144, 51, 5, 7);
    } else text('MY RECORDS. MY TASTE.', 122, 127, 51, 10, 10);
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 0.3 / mm;
    for (const left of [31, 116])
      for (const x of [left, left + 63])
        for (const y of [65, 153]) {
          const dX = x === left ? -1 : 1,
            dY = y === 65 ? -1 : 1;
          ctx.beginPath();
          ctx.moveTo(x + dX * 4, y);
          ctx.lineTo(x + dX * 7, y);
          ctx.moveTo(x, y + dY * 4);
          ctx.lineTo(x, y + dY * 7);
          ctx.stroke();
        }
    text(
      'Print this single-sided sheet at 100%. Cut at the crop marks, then join the two faces.\nThe front includes 3 mm bleed. Printer color and trimming accuracy may vary.\nThe QR opens the public profile, not a private edition or an account sign-in.',
      31,
      174,
      155,
      30,
      10,
    );
    if (signal?.aborted) throw signal.reason ?? new Error('Cancelled');
    const rgba = ctx.getImageData(0, 0, canvas.width, canvas.height).data,
      rgb = new Uint8Array(canvas.width * canvas.height * 3);
    for (let i = 0, j = 0; i < rgba.length; i += 4) {
      rgb[j++] = rgba[i];
      rgb[j++] = rgba[i + 1];
      rgb[j++] = rgba[i + 2];
    }
    const stream = new Blob([rgb]).stream().pipeThrough(new CompressionStream('deflate'));
    const compressed = new Uint8Array(await new Response(stream).arrayBuffer());
    if (signal?.aborted) throw signal.reason ?? new Error('Cancelled');
    return imagePdf(compressed, canvas.width, canvas.height);
  } finally {
    image.close();
  }
}
